// Helpers for unwrapping the two BE response envelope shapes the API can return:
//
//   1. a bare payload:        [...]            or  { ... }
//   2. a wrapped payload:     { data: [...] }  or  { data: { ... } }
//
// Centralizing these keeps every *.api.js adapter consistent and gives one place
// to change if the envelope is ever stabilized. See API_CONTRACT.md.

/**
 * Unwraps a list response. Accepts a bare array or a `{ data: [...] }` envelope.
 * Falls back to an empty array for any other shape (null/undefined/object).
 *
 * @param {{ data: any }} res - the axios response
 * @returns {any[]}
 */
export function unwrapList(res) {
  const d = res?.data;
  return Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
}

/**
 * Unwraps a single-object response. Accepts a bare object or a `{ data: {...} }`
 * envelope. Returns the inner `data` when present, otherwise the payload itself.
 *
 * @param {{ data: any }} res - the axios response
 * @returns {any}
 */
export function unwrapSingle(res) {
  const d = res?.data;
  return d?.data ?? d;
}

/**
 * Unwraps a paginated list response: `{ data: [...], pagination: {...} }`.
 * Returns `{ rows, pagination }`.
 *
 * Back-compat: if the BE returns the old bare/`{ data: [...] }` shape with no
 * `pagination` block, `pagination` is `null` and the caller can fall back to
 * client-side pagination. This lets the FE work before the paginated BE ships.
 *
 * @param {{ data: any }} res - the axios response
 * @returns {{ rows: any[], pagination: { page:number, pageSize:number, total:number, totalPages:number, hasMore:boolean } | null }}
 */
export function unwrapPage(res) {
  const d = res?.data;
  const rows = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
  const p = d?.pagination;
  const pagination = p && typeof p === 'object'
    ? {
        page: Number(p.page) || 1,
        pageSize: Number(p.pageSize) || rows.length,
        total: Number(p.total) || 0,
        totalPages: Number(p.totalPages) || 0,
        hasMore: !!p.hasMore,
      }
    : null;
  return { rows, pagination };
}
